import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Scale, Play, RefreshCw, FileDown, CheckCircle2, AlertTriangle,
  Loader2, ChevronLeft, FileSearch, Clock, FolderX, RotateCcw,
  BarChart3, LayoutList, ExternalLink, Lock,
} from 'lucide-react';
import EstadisticasAuditoria from '../components/EstadisticasAuditoria';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUD_PER_PAGE = 8;

const SEMAFORO = {
  verde: { color: 'bg-green-500', label: 'En plazo', text: 'text-green-700' },
  ambar: { color: 'bg-amber-500', label: 'Atención', text: 'text-amber-700' },
  rojo: { color: 'bg-red-600', label: 'Riesgo', text: 'text-red-700' },
  desconocido: { color: 'bg-gray-400', label: 'N/D', text: 'text-gray-600' },
};

const Semaforo = ({ valor }) => {
  const s = SEMAFORO[valor] || SEMAFORO.desconocido;
  return (
    <span className="inline-flex items-center gap-1.5" data-testid={`semaforo-${valor}`}>
      <span className={`inline-block w-2.5 h-2.5 rounded-full ${s.color}`} />
      <span className={`text-xs font-medium ${s.text}`}>{s.label}</span>
    </span>
  );
};

export default function AuditoriasProcesales() {
  const [sesiones, setSesiones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modelos, setModelos] = useState([]);
  const [showLanzar, setShowLanzar] = useState(false);
  const [sesionAbierta, setSesionAbierta] = useState(null);
  const [auditoriaAbierta, setAuditoriaAbierta] = useState(null);
  const [audPage, setAudPage] = useState(0);
  const [vista, setVista] = useState('sesiones');

  const [form, setForm] = useState({
    nombre: '', modo: 'todos', expediente_id: '', rango_desde: '', rango_hasta: '',
    modelo: 'gemini|gemini-3.1-pro-preview',
  });
  const [lanzando, setLanzando] = useState(false);

  const cargarSesiones = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auditorias-procesales/sesiones`);
      setSesiones(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarSesiones();
    axios.get(`${API}/auditorias-procesales/modelos`).then(r => setModelos(r.data || [])).catch(() => {});
  }, [cargarSesiones]);

  // Polling mientras haya sesiones en proceso
  useEffect(() => {
    const hayActivas = sesiones.some(s => ['pendiente', 'procesando'].includes(s.estado));
    if (!hayActivas) return;
    const id = setInterval(() => {
      cargarSesiones();
      if (sesionAbierta) abrirSesion(sesionAbierta.id, true);
    }, 4000);
    return () => clearInterval(id);
  }, [sesiones, sesionAbierta]);

  const lanzar = async () => {
    setLanzando(true);
    try {
      const [provider, name] = form.modelo.split('|');
      const body = {
        nombre: form.nombre || undefined,
        modo: form.modo,
        modelo_provider: provider,
        modelo_name: name,
      };
      if (form.modo === 'single') body.expediente_id = parseInt(form.expediente_id, 10);
      if (form.modo === 'rango') {
        body.rango_desde = form.rango_desde ? parseInt(form.rango_desde, 10) : null;
        body.rango_hasta = form.rango_hasta ? parseInt(form.rango_hasta, 10) : null;
      }
      await axios.post(`${API}/auditorias-procesales/sesiones`, body);
      toast.success('Auditoría lanzada. Se procesará en segundo plano.');
      setShowLanzar(false);
      setForm({ ...form, expediente_id: '', rango_desde: '', rango_hasta: '' });
      cargarSesiones();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al lanzar la auditoría');
    } finally {
      setLanzando(false);
    }
  };

  const abrirSesion = async (id, silent = false) => {
    try {
      const r = await axios.get(`${API}/auditorias-procesales/sesiones/${id}`);
      setSesionAbierta(r.data);
      if (!silent) setAudPage(0);
    } catch (e) {
      if (!silent) toast.error('No se pudo abrir la sesión');
    }
  };

  const abrirAuditoria = async (id) => {
    try {
      const r = await axios.get(`${API}/auditorias-procesales/${id}`);
      setAuditoriaAbierta(r.data);
    } catch (e) {
      toast.error('No se pudo abrir la auditoría');
    }
  };

  const descargarInforme = async (aud) => {
    try {
      const r = await axios.get(`${API}/auditorias-procesales/${aud.id}/informe`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `auditoria_${aud.numero_expediente || aud.id}.md`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('No se pudo descargar el informe');
    }
  };

  const reanalizar = async (aud) => {
    if (!window.confirm('¿Volver a analizar este expediente con IA? Se actualizará el resultado.')) return;
    try {
      await axios.post(`${API}/auditorias-procesales/${aud.id}/reanalizar`);
      toast.success('Re-análisis lanzado. Se actualizará en unos segundos.');
      setAuditoriaAbierta(null);
      if (sesionAbierta) abrirSesion(sesionAbierta.id, true);
      cargarSesiones();
    } catch (e) {
      toast.error('No se pudo relanzar el análisis');
    }
  };

  const cerrarExpediente = async (aud) => {
    if (!window.confirm('¿Marcar este expediente como CERRADO definitivamente? La IA dejará de analizarlo y se dará por auditado.')) return;
    try {
      await axios.post(`${API}/auditorias-procesales/${aud.id}/cerrar`);
      toast.success('Expediente cerrado. Se da por auditado.');
      setAuditoriaAbierta(null);
      if (sesionAbierta) abrirSesion(sesionAbierta.id, true);
      cargarSesiones();
    } catch (e) {
      toast.error('No se pudo cerrar el expediente');
    }
  };

  const resolverPropuesta = async (pid, accion) => {
    try {
      await axios.post(`${API}/auditorias-procesales/propuestas/${pid}/${accion}`);
      toast.success(accion === 'aprobar' ? 'Propuesta aprobada' : 'Propuesta descartada');
      abrirAuditoria(auditoriaAbierta.id);
    } catch (e) {
      toast.error('No se pudo actualizar la propuesta');
    }
  };

  const borrarSesion = async (id) => {
    if (!window.confirm('¿Eliminar esta sesión de auditoría y sus resultados?')) return;
    try {
      await axios.delete(`${API}/auditorias-procesales/sesiones/${id}`);
      toast.success('Sesión eliminada');
      setSesionAbierta(null);
      cargarSesiones();
    } catch (e) {
      toast.error('No se pudo eliminar');
    }
  };

  const estadoBadge = (estado) => {
    const map = {
      completada: 'bg-green-100 text-green-800',
      procesando: 'bg-blue-100 text-blue-800',
      pendiente: 'bg-gray-100 text-gray-700',
      error: 'bg-red-100 text-red-800',
      cancelada: 'bg-gray-200 text-gray-600',
    };
    return <Badge className={map[estado] || map.pendiente}>{estado}</Badge>;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="auditorias-procesales-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Scale className="w-7 h-7 text-slate-700" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Auditorías Procesales por IA</h1>
            <p className="text-sm text-slate-500">Análisis del estado procesal y de cobros de los expedientes</p>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50 mr-1" data-testid="vista-toggle">
            <button
              onClick={() => setVista('sesiones')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${vista === 'sesiones' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="tab-sesiones">
              <LayoutList className="w-4 h-4" /> Sesiones
            </button>
            <button
              onClick={() => setVista('estadisticas')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${vista === 'estadisticas' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="tab-estadisticas">
              <BarChart3 className="w-4 h-4" /> Estadísticas
            </button>
          </div>
          {vista === 'sesiones' && (
            <Button variant="outline" onClick={cargarSesiones} data-testid="refrescar-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refrescar
            </Button>
          )}
          <Button onClick={() => setShowLanzar(true)} data-testid="lanzar-auditoria-btn">
            <Play className="w-4 h-4 mr-2" /> Lanzar auditoría
          </Button>
        </div>
      </div>

      {vista === 'estadisticas' ? (
        <EstadisticasAuditoria />
      ) : loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando...
        </div>
      ) : sesiones.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-slate-500">
            <FileSearch className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>No hay auditorías todavía. Pulsa <b>Lanzar auditoría</b> para empezar.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {sesiones.map((s) => {
            const pct = s.total ? Math.round((s.procesados / s.total) * 100) : 0;
            return (
              <Card key={s.id} className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => abrirSesion(s.id)} data-testid={`sesion-card-${s.id}`}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-800 truncate">{s.nombre}</span>
                        {estadoBadge(s.estado)}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {s.modelo_provider}/{s.modelo_name} · {new Date(s.created_at).toLocaleString('es-ES')}
                      </p>
                    </div>
                    <div className="flex items-center gap-6 shrink-0">
                      <div className="text-center">
                        <p className="text-xs text-slate-400">Progreso</p>
                        <p className="font-semibold">{s.procesados}/{s.total}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-slate-400">Con hallazgos</p>
                        <p className="font-semibold text-amber-600">{s.con_hallazgos}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-slate-400">Errores</p>
                        <p className="font-semibold text-red-600">{s.errores}</p>
                      </div>
                    </div>
                  </div>
                  {['procesando', 'pendiente'].includes(s.estado) && (
                    <div className="w-full h-1.5 bg-slate-100 rounded mt-3 overflow-hidden">
                      <div className="h-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog: lanzar */}
      <Dialog open={showLanzar} onOpenChange={setShowLanzar}>
        <DialogContent className="max-w-lg" data-testid="lanzar-dialog">
          <DialogHeader>
            <DialogTitle>Lanzar auditoría procesal</DialogTitle>
            <DialogDescription>La IA analizará la documentación de cada expediente y su estado de cobros.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre (opcional)</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                     placeholder="Ej. Revisión trimestral" data-testid="input-nombre" />
            </div>
            <div>
              <Label>Alcance</Label>
              <Select value={form.modo} onValueChange={(v) => setForm({ ...form, modo: v })}>
                <SelectTrigger data-testid="select-modo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los expedientes</SelectItem>
                  <SelectItem value="rango">Rango (por Nº interno)</SelectItem>
                  <SelectItem value="single">Un solo expediente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.modo === 'single' && (
              <div>
                <Label>ID del expediente</Label>
                <Input type="number" value={form.expediente_id}
                       onChange={(e) => setForm({ ...form, expediente_id: e.target.value })}
                       data-testid="input-expediente-id" />
              </div>
            )}
            {form.modo === 'rango' && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Desde</Label>
                  <Input type="number" value={form.rango_desde}
                         onChange={(e) => setForm({ ...form, rango_desde: e.target.value })} data-testid="input-desde" /></div>
                <div><Label>Hasta</Label>
                  <Input type="number" value={form.rango_hasta}
                         onChange={(e) => setForm({ ...form, rango_hasta: e.target.value })} data-testid="input-hasta" /></div>
              </div>
            )}
            <div>
              <Label>Modelo de IA</Label>
              <Select value={form.modelo} onValueChange={(v) => setForm({ ...form, modelo: v })}>
                <SelectTrigger data-testid="select-modelo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {modelos.map((m) => (
                    <SelectItem key={`${m.provider}|${m.name}`} value={`${m.provider}|${m.name}`}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLanzar(false)}>Cancelar</Button>
            <Button onClick={lanzar} disabled={lanzando} data-testid="confirmar-lanzar-btn">
              {lanzando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              Lanzar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: detalle sesión */}
      <Dialog open={!!sesionAbierta} onOpenChange={(o) => !o && setSesionAbierta(null)}>
        <DialogContent className="max-w-[90vw] w-[90vw] max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="sesion-dialog">
          {sesionAbierta && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between pr-6">
                  <span>{sesionAbierta.nombre}</span>
                  <Button size="sm" variant="ghost" className="text-red-600"
                          onClick={() => borrarSesion(sesionAbierta.id)}>Eliminar</Button>
                </DialogTitle>
                <DialogDescription>
                  {sesionAbierta.completados}/{sesionAbierta.total} completados · {sesionAbierta.con_hallazgos} con hallazgos · {sesionAbierta.sin_adjuntos || 0} sin archivos · {sesionAbierta.errores} errores
                </DialogDescription>
              </DialogHeader>
              <div className="border rounded-lg divide-y">
                {(sesionAbierta.auditorias || []).slice(audPage * AUD_PER_PAGE, (audPage + 1) * AUD_PER_PAGE).map((a) => (
                  <div key={a.id} className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-slate-50"
                       data-testid={`auditoria-row-${a.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <a
                          href={`/expedientes/${a.expediente_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="font-medium text-sm text-blue-700 hover:text-blue-900 hover:underline inline-flex items-center gap-1"
                          data-testid={`exp-link-numero-${a.expediente_id}`}
                          title="Abrir expediente en una ventana nueva"
                        >
                          {a.numero_expediente || `Exp #${a.expediente_id}`}
                          <ExternalLink className="w-3 h-3 shrink-0" />
                        </a>
                        {a.estado === 'completada'
                          ? (a.veredicto === 'cerrado_usuario'
                              ? <Badge className="bg-slate-200 text-slate-700 flex items-center gap-1" data-testid={`badge-cerrado-${a.id}`}><Lock className="w-3 h-3" /> Cerrado por el usuario</Badge>
                              : a.finalizado
                                ? <Badge className="bg-green-100 text-green-800">Finalizado</Badge>
                                : <Badge className="bg-amber-100 text-amber-800">Pendiente revisión</Badge>)
                          : estadoBadge(a.estado)}
                        {a.alerta_adjuntos && (
                          <Badge className="bg-red-100 text-red-800 flex items-center gap-1" data-testid={`alerta-adjuntos-${a.id}`}>
                            <FolderX className="w-3 h-3" /> Sin archivos
                          </Badge>
                        )}
                      </div>
                      {a.estado === 'completada'
                        ? <p className="text-xs text-slate-600 line-clamp-2">{a.resumen || a.recomendacion || a.tipo_procedimiento || '—'}</p>
                        : <p className="text-xs text-slate-500 truncate">{a.error_msg || 'Procesando…'}</p>}
                    </div>
                    <Semaforo valor={a.semaforo} />
                    <Button size="sm" variant="outline" onClick={() => abrirAuditoria(a.id)}
                            disabled={a.estado !== 'completada'} data-testid={`ver-auditoria-${a.id}`}>Ver</Button>
                  </div>
                ))}
              </div>
              {(() => {
                const total = (sesionAbierta.auditorias || []).length;
                const totalPages = Math.ceil(total / AUD_PER_PAGE) || 1;
                if (totalPages <= 1) return null;
                return (
                  <div className="flex items-center justify-between mt-3" data-testid="aud-paginacion">
                    <Button size="sm" variant="outline" disabled={audPage === 0}
                            onClick={() => setAudPage(p => Math.max(0, p - 1))} data-testid="aud-page-prev">
                      <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
                    </Button>
                    <span className="text-xs text-slate-500" data-testid="aud-page-info">
                      Página {audPage + 1} de {totalPages} · {total} resultados
                    </span>
                    <Button size="sm" variant="outline" disabled={audPage >= totalPages - 1}
                            onClick={() => setAudPage(p => Math.min(totalPages - 1, p + 1))} data-testid="aud-page-next">
                      Siguiente
                    </Button>
                  </div>
                );
              })()}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: detalle auditoría */}
      <Dialog open={!!auditoriaAbierta} onOpenChange={(o) => !o && setAuditoriaAbierta(null)}>
        <DialogContent className="max-w-[90vw] w-[90vw] max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="auditoria-dialog">
          {auditoriaAbierta && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <button onClick={() => setAuditoriaAbierta(null)} className="text-slate-400 hover:text-slate-700">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <a
                    href={`/expedientes/${auditoriaAbierta.expediente_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 hover:text-blue-900 hover:underline inline-flex items-center gap-1"
                    data-testid={`exp-link-numero-detalle-${auditoriaAbierta.expediente_id}`}
                    title="Abrir expediente en una ventana nueva"
                  >
                    {auditoriaAbierta.numero_expediente || `Exp #${auditoriaAbierta.expediente_id}`}
                    <ExternalLink className="w-4 h-4 shrink-0" />
                  </a>
                  <Semaforo valor={auditoriaAbierta.semaforo} />
                  <Button size="sm" variant="outline" className="ml-auto mr-6" onClick={() => reanalizar(auditoriaAbierta)}
                          data-testid="reanalizar-btn">
                    <RotateCcw className="w-4 h-4 mr-1.5" /> Re-analizar
                  </Button>
                  {auditoriaAbierta.veredicto !== 'cerrado_usuario' && (
                    <Button size="sm" variant="outline" className="mr-6 text-slate-700 border-slate-300"
                            onClick={() => cerrarExpediente(auditoriaAbierta)} data-testid="cerrar-expediente-btn">
                      <Lock className="w-4 h-4 mr-1.5" /> Marcar como CERRADO
                    </Button>
                  )}
                </DialogTitle>
                <DialogDescription>
                  {auditoriaAbierta.jurisdiccion} · {auditoriaAbierta.tipo_procedimiento} · {auditoriaAbierta.fase_actual}
                </DialogDescription>
              </DialogHeader>

              {/* Resumen destacado */}
              {auditoriaAbierta.resumen && (
                <div className="p-3 bg-slate-50 border-l-4 border-slate-400 rounded mb-3" data-testid="resumen-destacado">
                  <p className="text-sm text-slate-700 line-clamp-[10]" data-testid="resumen-destacado-texto">{auditoriaAbierta.resumen}</p>
                  {auditoriaAbierta.recomendacion && (
                    <p className="text-sm text-slate-600 mt-1.5"><b>Recomendación:</b> {auditoriaAbierta.recomendacion}</p>
                  )}
                </div>
              )}

              {/* Alerta de archivos adjuntos faltantes */}
              {auditoriaAbierta.alerta_adjuntos && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-3 flex items-start gap-2" data-testid="alerta-adjuntos-detalle">
                  <FolderX className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-red-800">Posible migración sin archivos</p>
                    <p className="text-sm text-red-700">{auditoriaAbierta.adjuntos_info}</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 mb-3">
                <Card><CardContent className="py-3">
                  <p className="text-xs text-slate-400">Veredicto de cobros</p>
                  <p className={`font-semibold ${auditoriaAbierta.veredicto === 'cerrado_usuario' ? 'text-slate-700' : auditoriaAbierta.finalizado ? 'text-green-700' : 'text-amber-700'}`}>
                    {auditoriaAbierta.veredicto === 'cerrado_usuario'
                      ? 'Cerrado por el usuario (dado por auditado)'
                      : auditoriaAbierta.finalizado ? 'Finalizado (todo cobrado)' : 'Pendiente de revisión'}
                  </p>
                </CardContent></Card>
                <Card><CardContent className="py-3">
                  <p className="text-xs text-slate-400">Próxima actuación</p>
                  <p className="text-sm text-slate-700">{auditoriaAbierta.proxima_actuacion || '—'}</p>
                </CardContent></Card>
              </div>

              {/* Cobros */}
              {auditoriaAbierta.pagos_analisis && (
                <div className="mb-3">
                  <p className="text-sm font-semibold text-slate-700 mb-1.5">Análisis de cobros</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {Object.entries(auditoriaAbierta.pagos_analisis).map(([k, v]) => (
                      <div key={k} className="flex items-center gap-2 text-sm">
                        {v?.cobrado ? <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" /> : <Clock className="w-4 h-4 text-amber-500 shrink-0" />}
                        <span className="text-slate-600 capitalize">{k.replace(/_/g, ' ')}</span>
                        {v?.nota && <span className="text-xs text-slate-400 truncate">— {v.nota}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Trámites faltantes */}
              {Array.isArray(auditoriaAbierta.tramites_faltantes) && auditoriaAbierta.tramites_faltantes.length > 0 && (
                <div className="mb-3 p-3 bg-amber-50 rounded-lg">
                  <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5">
                    <AlertTriangle className="w-4 h-4" /> Trámites/pendientes detectados
                  </p>
                  <ul className="list-disc list-inside text-sm text-amber-700 mt-1">
                    {auditoriaAbierta.tramites_faltantes.map((t, i) => <li key={i}>{t}</li>)}
                  </ul>
                </div>
              )}

              {/* Propuestas revisables */}
              {Array.isArray(auditoriaAbierta.propuestas) && auditoriaAbierta.propuestas.length > 0 && (
                <div className="mb-3">
                  <p className="text-sm font-semibold text-slate-700 mb-1.5">Propuestas a incorporar (revisables)</p>
                  <div className="space-y-2">
                    {auditoriaAbierta.propuestas.map((p) => (
                      <div key={p.id} className="flex items-start justify-between gap-3 p-2.5 border rounded-lg"
                           data-testid={`propuesta-${p.id}`}>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">{p.categoria}</Badge>
                            <span className="text-sm font-medium">{p.titulo}</span>
                          </div>
                          <p className="text-xs text-slate-500">{p.descripcion}</p>
                        </div>
                        {p.estado === 'pendiente' ? (
                          <div className="flex gap-1.5 shrink-0">
                            <Button size="sm" variant="outline" className="text-green-700"
                                    onClick={() => resolverPropuesta(p.id, 'aprobar')}>Aprobar</Button>
                            <Button size="sm" variant="ghost" className="text-red-600"
                                    onClick={() => resolverPropuesta(p.id, 'descartar')}>Descartar</Button>
                          </div>
                        ) : <Badge className="shrink-0">{p.estado}</Badge>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Informe */}
              <div className="mb-2">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-sm font-semibold text-slate-700">Informe</p>
                  <Button size="sm" variant="outline" onClick={() => descargarInforme(auditoriaAbierta)}>
                    <FileDown className="w-4 h-4 mr-1.5" /> Descargar .md
                  </Button>
                </div>
                <pre className="text-xs bg-slate-50 p-3 rounded-lg whitespace-pre-wrap font-mono text-slate-700 max-h-72 overflow-y-auto">
                  {auditoriaAbierta.informe_md || 'Sin informe.'}
                </pre>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
